/**
 * PrashantWeb Color Token System
 * ─────────────────────────────────────────────────────────────────────────────
 * SINGLE SOURCE OF TRUTH for all domain / trading colors.
 *
 * Rule: Components must NEVER use raw hex values inline.
 *       They import `tokens` and select the mode:
 *
 *   import { useTheme } from '@mui/material';
 *   import { tokens } from '../../styles/colorTokens';
 *   const theme = useTheme();
 *   const C = tokens[theme.palette.mode];   // C.bull, C.bear, ...
 *
 * Design principle:
 *   Dark mode  → bright / neon variants  (readable on near-black backgrounds)
 *   Light mode → deep / saturated variants (readable on white, contrast ≥ 4.5:1)
 *
 * See COLOR_SYSTEM.md for full rationale and component mapping.
 */

export interface AppColorTokens {
  // ── Bullish: Long / Buy / Profit / Approved ─────────────────────────────
  bull:         string;
  bullMuted:    string;  // chip / row background
  bullBorder:   string;  // chip border

  // ── Bearish: Short / Sell / Loss / Rejected ──────────────────────────────
  bear:         string;
  bearMuted:    string;
  bearBorder:   string;

  // ── Caution: Warning / Snoozed / Amber / Pending ────────────────────────
  warn:         string;
  warnMuted:    string;
  warnBorder:   string;

  // ── Neutral / Info: CE/Calls / Data / Unknown ────────────────────────────
  neutral:      string;
  neutralMuted: string;
  neutralBorder:string;

  // ── Accent / Special: Max Pain / Spike Trail / Premium ──────────────────
  accent:       string;
  accentMuted:  string;
  accentBorder: string;

  // ── Dim / Disabled / Closed / Offline ───────────────────────────────────
  dim:          string;
  dimMuted:     string;

  /**
   * Text colour for a SOLID token background (a filled chip or button).
   *
   * Not the same question as the token itself. In dark mode every token is BRIGHT so it can
   * carry on near-black — which means white text on top of one is close to unreadable: white
   * on the dark bull measures 1.89:1, on warn 1.67:1, on bear 2.71:1. In light mode the tokens
   * are deep and white is correct. Components had hardcoded '#fff' for both, so half of them
   * were always wrong.
   */
  onSolid:      string;

  // ── Options Domain ───────────────────────────────────────────────────────
  callText:     string;  // CE column text / header
  callBg:       string;  // CE column background tint
  putText:      string;  // PE column text / header
  putBg:        string;  // PE column background tint
  atm:          string;  // ATM strike text / border
  atmBg:        string;  // ATM row background
  maxPain:      string;  // Max Pain level marker
  spot:         string;  // Spot price reference line

  // ── Chart Colors (for Recharts fill= / stroke= props) ───────────────────
  chartBull:           string;
  chartBear:           string;
  chartLine1:          string;  // primary series line
  chartLine2:          string;  // secondary series line
  chartGrid:           string;  // grid lines
  chartTooltipBg:      string;
  chartTooltipBorder:  string;

  // ── Row Backgrounds ──────────────────────────────────────────────────────
  rowProfit:    string;   // trade row: profit / long
  rowLoss:      string;   // trade row: loss / short
  rowPending:   string;   // trade row: open / pending
}

const light: AppColorTokens = {
  // Bullish. Darkened from #0A7E52 (2026-09-03). The old note said "contrast >= 7:1 on white"
  // and that was true — but almost nothing in this app sits on pure white. A LONG chip painted
  // bullMuted inside a neutralMuted card lands on rgb(193,217,223), where it measured 3.46:1.
  // Measuring against white is what hid it. Now 5.60:1 at its worst.
  bull:         '#07593A',
  bullMuted:    'rgba(7,89,58,0.12)',
  bullBorder:   'rgba(7,89,58,0.35)',

  // Bearish. Darkened from #B91C1C (2026-09-03): 4.19:1 on a tinted chip inside a tinted card
  // ("Negative expectancy" on the option desk). Now 5.46:1 at its worst.
  bear:         '#991717',
  bearMuted:    'rgba(153,23,23,0.12)',
  bearBorder:   'rgba(153,23,23,0.35)',

  // Caution / Amber. Darkened from #B45309 (2026-09-03) for the same reason as bull: 5.02:1 on
  // white but 3.35-3.89:1 on the tinted chips it is actually used in — grade chips, the
  // SHADOW MODE badge, "stands in for N". Now 5.01:1 at its worst.
  warn:         '#8A3F07',
  warnMuted:    'rgba(138,63,7,0.12)',
  warnBorder:   'rgba(138,63,7,0.35)',

  // Neutral / Info. Darkened from #1565C0 (2026-09-03): 3.98:1 on the tinted chips it is used
  // in (the option desk's ACCEPTABLE verdict), against 5.8 on white. Now 5.15:1 at its worst.
  neutral:      '#12559F',
  neutralMuted: 'rgba(18,85,159,0.10)',
  neutralBorder:'rgba(18,85,159,0.30)',

  // Accent / Purple (dark purple — contrast ≥ 5.1:1 on white)
  accent:       '#6B21A8',
  accentMuted:  'rgba(107,33,168,0.10)',
  accentBorder: 'rgba(107,33,168,0.30)',

  // Dim / Disabled
  // Raised from #64748B (2026-09-03). That cleared 4.76:1 on white but only 4.31:1 on the app's
  // actual page background (#F0F4F8) and 4.11-4.24:1 inside the chips that tint with its own
  // hue -- and almost nothing in this app sits on pure white. Now 5.82 / 5.27 / 4.95-5.13.
  // Darkened again to #4A5462 after the live audit. #5A6678 cleared 5.82:1 on white but only
  // 3.96-4.09:1 on the AI Desk's compound tints, where a dim chip sits inside an accent-tinted
  // panel. Same lesson as dark mode: the background that matters is the one it is drawn on.
  dim:          '#4A5462',
  dimMuted:     'rgba(74,84,98,0.10)',

  // Light tokens are deep, so white reads on them (>= 6:1 on every one).
  onSolid:      '#FFFFFF',

  // Options columns
  callText:     '#1565C0',
  callBg:       'rgba(21,101,192,0.06)',
  putText:      '#92400E',
  putBg:        'rgba(146,64,14,0.06)',
  atm:          '#B45309',
  atmBg:        'rgba(180,83,9,0.12)',
  maxPain:      '#6B21A8',
  spot:         '#B45309',

  // Charts
  chartBull:          '#0A7E52',
  chartBear:          '#B91C1C',
  chartLine1:         '#1565C0',
  chartLine2:         '#B45309',
  chartGrid:          '#CBD5E1',
  chartTooltipBg:     '#FFFFFF',
  chartTooltipBorder: '#CBD5E1',

  // Row backgrounds
  // Rebuilt from the current bull/bear/warn. They were still the pre-2026-09-03 rgb values,
  // so a row tint and the chips on it were drawn from two different palettes.
  rowProfit:   'rgba(7,89,58,0.06)',
  rowLoss:     'rgba(153,23,23,0.06)',
  rowPending:  'rgba(138,63,7,0.06)',
};

const dark: AppColorTokens = {
  // Bullish (bright teal — high visibility on near-black)
  bull:         '#2DD4AA',
  bullMuted:    'rgba(45,212,170,0.15)',
  bullBorder:   'rgba(45,212,170,0.40)',

  // Bearish (coral red — high visibility on dark)
  // Raised from #F25F6A (2026-09-03). It was the ONLY token failing AA on a compound
  // background: 3.96-4.35:1 for loss text inside a muted chip on a tinted panel, against
  // 5.45 on the plain card. Every other token cleared 5.07+ there, so this is a targeted
  // fix rather than a palette change; the hue is unchanged and readability only improves
  // (4.64-6.39 now). Loss red is load-bearing in a trading app, so it moved the minimum
  // needed and no further.
  bear:         '#F47681',
  // 0.12, not 0.15: the last two sub-AA elements in the app were C.bear on a bearMuted chip
  // nested in another tint, at 4.36:1. Lowering the tint gives 4.59 and brings this in line
  // with neutralMuted and dimMuted, which are already 0.12 -- the 0.15/0.12 split across the
  // muted set was never deliberate.
  bearMuted:    'rgba(244,118,129,0.12)',
  bearBorder:   'rgba(244,118,129,0.40)',

  // Caution / Amber
  warn:         '#FBBF24',
  warnMuted:    'rgba(251,191,36,0.15)',
  warnBorder:   'rgba(251,191,36,0.40)',

  // Neutral / Info
  neutral:      '#60A5FA',
  neutralMuted: 'rgba(96,165,250,0.12)',
  neutralBorder:'rgba(96,165,250,0.35)',

  // Accent / Purple
  accent:       '#A78BFA',
  accentMuted:  'rgba(167,139,250,0.15)',
  accentBorder: 'rgba(167,139,250,0.40)',

  // Dim / Disabled
  // Raised from #6E7681 (2026-09-03), which failed WCAG AA everywhere it was used: 3.77:1 on
  // the card surface, 3.20-3.42:1 inside chips that paint their background from its own hue at
  // 9-15% alpha. That token carries the AI Desk's stat labels, the scorecard footers and every
  // 'flat' RockBand chip, which is why that page read as unreadable in dark mode. Now 5.62:1 on
  // the card and 4.73-4.93:1 on the tints. dimMuted follows the new hue so the two stay in step.
  // Raised again to #949DA7 after the live audit: #8B949E cleared AA on the card surface but
  // only reached 4.08-4.48 inside a muted chip sitting on the AI Desk's accent-tinted panel,
  // where two translucent layers compound. Measuring against paper alone missed that.
  dim:          '#949DA7',
  dimMuted:     'rgba(148,157,167,0.12)',

  // Dark tokens are bright, so the readable text on them is the page's near-black.
  onSolid:      '#0D1117',

  // Options columns
  callText:     '#60A5FA',
  callBg:       'rgba(96,165,250,0.06)',
  putText:      '#F97316',
  putBg:        'rgba(249,115,22,0.06)',
  atm:          '#FBBF24',
  atmBg:        'rgba(251,191,36,0.12)',
  maxPain:      '#A78BFA',
  spot:         '#F59E0B',

  // Charts
  chartBull:          '#2DD4AA',
  chartBear:          '#F25F6A',
  chartLine1:         '#60A5FA',
  chartLine2:         '#FBBF24',
  chartGrid:          '#30363D',
  chartTooltipBg:     '#21262D',
  chartTooltipBorder: '#30363D',

  // Row backgrounds
  // Same: rebuilt from the current bear. bull and warn were already in step.
  rowProfit:   'rgba(45,212,170,0.08)',
  rowLoss:     'rgba(244,118,129,0.08)',
  rowPending:  'rgba(251,191,36,0.08)',
};

/** Color tokens indexed by MUI palette mode. Usage: `tokens[theme.palette.mode]` */
export const tokens: Record<'light' | 'dark', AppColorTokens> = { light, dark };
